/**
 * fetch wrappers for the /api/check-in-sessions/* endpoints exposed by
 * sm4rt-api. Matches the style of quotesApi.ts — plain functions
 * returning typed contracts, throwing on non-2xx, AbortSignal-aware.
 */

import type {
  CheckInDraftJob,
  CheckInPhotoShot,
  CheckInDraftJobCreate,
  CheckInDraftJobUpdate,
  CheckInSession,
  CheckInSessionCreate,
  CheckInSessionListResponse,
  CheckInSessionUpdate,
  CheckInAuditListResponse,
  CheckInCreateTicketsResponse,
  CheckInRetryResponse,
  CheckInPaymentDeferRequest,
  CheckInPaymentDeferResponse,
  CheckInContinueWithoutDigitalAcceptanceRequest,
  CheckInPrintTermsRequest,
  CheckInLifecycleStatus,
  CheckInIntakeType,
  CheckInCompletePreconditionFailure,
  CompleteCheckInSessionResponse,
  CheckInReturnListResponse,
  CheckInReturnSummary,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export interface FetchCheckInSessionsParams {
  page?: number;
  pageSize?: number;
  lifecycleStatus?: CheckInLifecycleStatus | CheckInLifecycleStatus[];
  intakeType?: CheckInIntakeType;
  customerPhone?: string;
  customerEmail?: string;
  /**
   * Server-side ordering. Allowlisted by the API — expiry-ascending (the
   * default) is right for active sessions, but a list of completed check-ins
   * wants the most recent first.
   */
  orderBy?: 'expiresAt' | 'createdAtDesc' | 'completedAtDesc';
  signal?: AbortSignal;
}

export async function fetchCheckInSessions(
  params: FetchCheckInSessionsParams,
): Promise<CheckInSessionListResponse> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.lifecycleStatus) {
    const value = Array.isArray(params.lifecycleStatus)
      ? params.lifecycleStatus.join(',')
      : params.lifecycleStatus;
    qs.set('lifecycleStatus', value);
  }
  if (params.intakeType) qs.set('intakeType', params.intakeType);
  if (params.customerPhone) qs.set('customerPhone', params.customerPhone);
  if (params.customerEmail) qs.set('customerEmail', params.customerEmail);
  if (params.orderBy) qs.set('orderBy', params.orderBy);

  const response = await fetch(`${API_BASE_URL}/check-in-sessions?${qs}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal: params.signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch check-in sessions: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}

export async function createCheckInSession(
  payload: CheckInSessionCreate,
): Promise<CheckInSession> {
  const response = await fetch(`${API_BASE_URL}/check-in-sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to create check-in session'));
  }
  return response.json();
}

export async function fetchCheckInSession(
  sessionId: string,
  signal?: AbortSignal,
): Promise<CheckInSession> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch check-in session ${sessionId}: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}

/**
 * Resolve a check-in by the human-facing session number (`YYYYMMDDNN`) —
 * the reference printed on the slip and quoted back to reception — the way
 * a ticket resolves from its RS number. Returns null on 404 so a mistyped
 * reference reads as "not found" rather than an error.
 */
export async function fetchCheckInSessionByNumber(
  sessionNumber: string,
  signal?: AbortSignal,
): Promise<CheckInSession | null> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/by-number/${encodeURIComponent(sessionNumber)}`,
    { headers: { Accept: 'application/json' }, signal },
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(
      `Failed to fetch check-in session ${sessionNumber}: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}

export async function patchCheckInSession(
  sessionId: string,
  patch: CheckInSessionUpdate,
): Promise<CheckInSession> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, `Failed to update session ${sessionId}`));
  }
  return response.json();
}

export async function cancelCheckInSession(
  sessionId: string,
  reason: string,
): Promise<CheckInSession> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/cancel`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, `Failed to cancel session ${sessionId}`));
  }
  return response.json();
}

export async function recoverCheckInSession(sessionId: string): Promise<CheckInSession> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/recover`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  );
  if (!response.ok) {
    throw new Error(await readError(response, `Failed to recover session ${sessionId}`));
  }
  return response.json();
}

export async function addCheckInDraftJob(
  sessionId: string,
  payload: CheckInDraftJobCreate,
): Promise<CheckInDraftJob> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/draft-jobs`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to add draft job'));
  }
  return response.json();
}

export async function patchCheckInDraftJob(
  sessionId: string,
  jobId: string,
  patch: CheckInDraftJobUpdate,
): Promise<CheckInDraftJob> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/draft-jobs/${encodeURIComponent(jobId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, `Failed to update draft job ${jobId}`));
  }
  return response.json();
}

export async function deleteCheckInDraftJob(
  sessionId: string,
  jobId: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/draft-jobs/${encodeURIComponent(jobId)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    throw new Error(await readError(response, `Failed to delete draft job ${jobId}`));
  }
}

export async function uploadCheckInPhoto(
  sessionId: string,
  jobId: string,
  file: File,
  /** Which guided shot this is. A retake of any shot but `area_of_interest`
   *  replaces the device's previous photo for it (server-side). */
  shot?: CheckInPhotoShot,
): Promise<{ id: string; blobUrl: string; signedUrl?: string }> {
  const headers: Record<string, string> = {
    'Content-Type': file.type || 'application/octet-stream',
    'X-Filename': file.name,
  };
  if (shot) headers['X-Photo-Shot'] = shot;
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/draft-jobs/${encodeURIComponent(jobId)}/photos`,
    { method: 'POST', headers, body: file },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to upload photo'));
  }
  return response.json();
}

/** Describe what an area-of-interest photo shows. Blank clears it. */
export async function updateCheckInPhotoCaption(
  sessionId: string,
  jobId: string,
  photoId: string,
  caption: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/draft-jobs/${encodeURIComponent(jobId)}/photos/${encodeURIComponent(photoId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ caption }),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to save the description'));
  }
}

export async function deleteCheckInPhoto(
  sessionId: string,
  jobId: string,
  photoId: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/draft-jobs/${encodeURIComponent(jobId)}/photos/${encodeURIComponent(photoId)}`,
    { method: 'DELETE' },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to delete photo'));
  }
}

export async function markCheckInPaid(
  sessionId: string,
  paymentReference: string,
): Promise<CheckInSession> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/payment/mark-paid`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentReference }),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to mark paid'));
  }
  return response.json();
}

/**
 * Defer (Existing / New customer) or Not Required — the replacement for the
 * old payment override. A 409 on `new_customer` means the invoice isn't in
 * Xero yet (or doesn't exist): nothing changed, press again in a moment.
 */
export async function deferCheckInPayment(
  sessionId: string,
  payload: CheckInPaymentDeferRequest,
): Promise<CheckInPaymentDeferResponse> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/payment/defer`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to update payment'));
  }
  return response.json();
}

/** The print service's answer: `published`, or `skipped` when no printer is
 *  set up for the lane (nothing printed). */
export interface CheckInPrintTermsResult {
  published: boolean;
  skipped?: boolean;
  reason?: string;
  role?: string;
}

/** Print the check-in's terms on the counter receipt printer or A4. */
export async function printCheckInTerms(
  sessionId: string,
  target: CheckInPrintTermsRequest['target'],
): Promise<CheckInPrintTermsResult> {
  const body: CheckInPrintTermsRequest = { target };
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/terms/print`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to print the terms'));
  }
  return response.json();
}

/** Continue the check-in after handing the customer printed terms. Never an
 *  acceptance — the session records a terms notice instead. */
export async function continueWithoutDigitalAcceptance(
  sessionId: string,
  payload: CheckInContinueWithoutDigitalAcceptanceRequest,
): Promise<CheckInSession> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/terms/continue-without-digital-acceptance`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to continue the check-in'));
  }
  return response.json();
}

export async function retryCheckInOrchestration(
  sessionId: string,
): Promise<CheckInRetryResponse> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/orchestration/retry`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to retry orchestration'));
  }
  return response.json();
}

/**
 * Reception's Create Tickets action — raises the RepairShopr tickets for the
 * devices captured so far.
 *
 * Separate from the terms-acceptance orchestration because "Start on wall"
 * shows the QR before any device is typed in, so the customer can accept the
 * terms while reception still has the machine. Gated by
 * `session.ticketCreation.canCreate`; needs a matched customer and at least
 * one device without a ticket, and deliberately not a finished inspection.
 */
export async function createCheckInTickets(
  sessionId: string,
): Promise<CheckInCreateTicketsResponse> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/tickets`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to create tickets'));
  }
  return response.json();
}

/**
 * Thrown by `completeCheckInSession` when the server returns 409 because one
 * or more draft jobs still need a picker decision. UI callers should catch
 * this and surface the affected ids inline.
 */
export class CheckInCompletePreconditionError extends Error {
  readonly unresolvedDraftJobIds: string[];
  constructor(failure: CheckInCompletePreconditionFailure) {
    super(`Ticket assignment incomplete for ${failure.unresolvedDraftJobIds.length} device(s)`);
    this.name = 'CheckInCompletePreconditionError';
    this.unresolvedDraftJobIds = failure.unresolvedDraftJobIds;
  }
}

/**
 * Complete a check-in. The response is the updated session. Completing prints
 * nothing: check-in paperwork goes out once each ticket's Dataverse job exists
 * (sm4rt-api's CheckInAutoPrintService), recorded as `document_printed`.
 */
export async function completeCheckInSession(
  sessionId: string,
): Promise<CompleteCheckInSessionResponse> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/complete`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' },
  );
  if (response.status === 409) {
    const body = (await response.json().catch(() => null)) as CheckInCompletePreconditionFailure | null;
    if (body && body.error === 'ticket_assignment_incomplete') {
      throw new CheckInCompletePreconditionError(body);
    }
  }
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to complete session'));
  }
  return response.json();
}

export async function fetchReturnsForWorkItem(
  workItemId: string,
  signal?: AbortSignal,
): Promise<CheckInReturnSummary[]> {
  const response = await fetch(
    `${API_BASE_URL}/workitems/${encodeURIComponent(workItemId)}/returns`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to load returns for work item'));
  }
  const body = (await response.json()) as CheckInReturnListResponse;
  return body.items ?? [];
}

export async function fetchCheckInAudit(
  sessionId: string,
  signal?: AbortSignal,
): Promise<CheckInAuditListResponse> {
  const response = await fetch(
    `${API_BASE_URL}/check-in-sessions/${encodeURIComponent(sessionId)}/audit`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch audit: ${response.status}`);
  }
  return response.json();
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.error === 'string') return body.error;
  } catch {
    /* not JSON */
  }
  return `${fallback}: ${response.status} ${response.statusText}`;
}
